import type { Channel } from '../../../utils';
import type { Solution } from '../../../xstate/prostudio.xstate.types';

export interface CredentialModalOption {
  /** The `<SelectOption>` value — also the `id` the Select's typeahead matches on. */
  id: string;
  label: string;
}

/**
 * Solutions the user can scope the Channel list to (PSTUDIO-3829).
 *
 * Keyed by `botSolutionId`, not `solution.id`: a channel records the bot that
 * starts its chat (`Channel.botId`), and that is what `botSolutionId` maps to.
 * Solutions without one can't be matched against any channel, so they would offer
 * an always-empty Channel list — dropped rather than shown as a dead end.
 *
 * Sorted by label because the list can run to 100+ entries on some projects, and
 * the Select's own typeahead re-sorts alphabetically once a search is cleared —
 * so an unsorted initial order would visibly reshuffle on first use.
 */
export const getSolutionOptions = (
  solutions?: Solution[]
): CredentialModalOption[] =>
  (solutions ?? [])
    .filter((solution) => !!solution.botSolutionId)
    .map((solution) => {
      const id = solution.botSolutionId as string;
      return { id, label: solution.displayName || solution.name || id };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Channels of one solution the user can scope a credential to (PSTUDIO-2990).
 *
 * The option's value is the pype id — a credential IS pype-scoped — while the
 * label is the channel name, because "pype" reads as an opaque id to the user and
 * repeats for every channel behind the same pype.
 *
 * Deduped by pype id: a pype can back several channels, and two options carrying
 * the same value would be an ambiguous choice. When that happens the names are
 * joined so the option still says what it covers.
 *
 * Returns nothing without a solution (PSTUDIO-3829). The Channel select is
 * disabled in that state, so an empty list is never what the user is looking at —
 * this replaced an implicit scope taken from the breadcrumb, which listed every
 * project channel whenever the page had been opened by a direct link.
 */
export const getChannelOptions = (
  channels: Channel[],
  solutionId: string
): CredentialModalOption[] => {
  if (!solutionId) return [];

  const byPype = new Map<string, string[]>();
  for (const channel of channels) {
    if (!channel.pypeId || channel.botId !== solutionId) continue;

    const names = byPype.get(channel.pypeId);
    if (names) {
      if (!names.includes(channel.name)) names.push(channel.name);
    } else {
      byPype.set(channel.pypeId, [channel.name]);
    }
  }

  return [...byPype.entries()].map(([id, names]) => ({
    id,
    label: names.filter(Boolean).join(', ') || id,
  }));
};
